import { ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { cn } from '../../../components/ui/utils';
import { getInactiveReason, getPhoneBadge } from '../lib/status';
import type { Account } from '../model/types';
import { platformLabel } from '../model/platforms';
import { PlatformIcon } from './PlatformIcon';

interface AccountCardProps {
  account: Account;
  /**
   * Слот для дополнительных действий (например, меню с тремя точками)
   * Позволяет композировать карточку с actions из features
   */
  actions?: React.ReactNode;
  onClick?: () => void;
  /** Профиль в самой сети — маленькой иконкой рядом с именем. */
  externalUrl?: string;
  /** Режим выбора: чекбокс в углу, клик по карточке переключает его. */
  selection?: { selected: boolean; onToggle: () => void };
  /** Строка под тэгами — например «Переподключить» у протухшей карточки. */
  footer?: React.ReactNode;
  /** Подсветка только что добавленной карточки. */
  highlighted?: boolean;
  /** Подписчики ещё грузятся (TikTok отдаёт их отдельным запросом). */
  followersLoading?: boolean;
}

/**
 * Карточка аккаунта любой сети (Entity - только отображение данных)
 * Отображает аватар, имя, статистику подписчиков и теги
 * Не содержит бизнес-логики - только UI
 */
export function AccountCard({
  account, actions, onClick, externalUrl, selection, footer, highlighted, followersLoading,
}: AccountCardProps) {
  const tags = account.tags || [];
  const platform = account.platform ?? 'instagram';

  const now = new Date();
  const expiry = account.accessTokenExpiry ? new Date(account.accessTokenExpiry) : null;
  const isTokenExpired = expiry ? expiry < now : false;
  const daysUntilExpiry = expiry ? Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const isExpiringSoon = !isTokenExpired && daysUntilExpiry !== null && daysUntilExpiry <= 7;
  const isInactive = getInactiveReason(account) !== null;
  const phoneBadge = getPhoneBadge(account);

  // Срок 0 / «1970» — токена нет вовсе: строку со сроком не показываем,
  // о неактивности и так говорят плашка и «Переподключить».
  const expiryLabel = expiry && !Number.isNaN(expiry.getTime()) && expiry.getFullYear() > 2000
    ? expiry.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const handleClick = selection ? selection.onToggle : onClick;

  return (
    <Card
      className={cn(
        'relative transition-shadow',
        handleClick && 'cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2',
        selection?.selected && 'ring-2 ring-primary ring-offset-2',
        highlighted && 'ring-2 ring-primary ring-offset-2 animate-pulse',
      )}
    >
      {/* Слот для actions; в режиме выбора меню не нужно — действия в общей панели */}
      {actions && !selection && (
        <div className="absolute top-4 right-4">
          {actions}
        </div>
      )}
      {selection && (
        <div className="absolute top-4 right-4">
          <Checkbox
            checked={selection.selected}
            onCheckedChange={selection.onToggle}
            aria-label={`Выбрать @${account.username || account.name}`}
          />
        </div>
      )}

      <CardContent className="p-4 space-y-3" onClick={handleClick}>
        {/* Информация об аккаунте */}
        <div className="flex items-center gap-3">
          {/* Аватар */}
          <Avatar className="h-12 w-12">
            <AvatarImage src={account.profilePicture} alt={account.name} />
            <AvatarFallback>
              <PlatformIcon platform={platform} className="h-6 w-6" />
            </AvatarFallback>
          </Avatar>

          {/* Имя и статистика */}
          <div className="flex-1 min-w-0 pr-8">
            <div className="flex items-center gap-2">
              <span title={platformLabel(platform)} className="shrink-0 text-muted-foreground">
                <PlatformIcon platform={platform} className="size-3.5" />
              </span>
              <h3 className="font-semibold truncate">{account.name}</h3>
              {externalUrl && (
                <a
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={`Открыть в ${platformLabel(platform)}`}
                  title={`Открыть в ${platformLabel(platform)}`}
                >
                  <ExternalLink className="size-3.5" />
                </a>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {isInactive && (
                <Badge variant="destructive" className="text-xs">
                  Не активен
                </Badge>
              )}
              {phoneBadge && (
                <Badge variant={phoneBadge.variant} className="text-xs">{phoneBadge.label}</Badge>
              )}
              <p className="text-sm text-muted-foreground">
                {followersLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin inline" />
                ) : (
                  `${account.followersCount !== undefined ? account.followersCount.toLocaleString('ru-RU') : '—'} подписчиков`
                )}
              </p>
            </div>
            {expiryLabel && (
              <p className={`text-xs mt-0.5 ${
                isTokenExpired
                  ? 'text-destructive'
                  : isExpiringSoon
                    ? 'text-yellow-500'
                    : 'text-muted-foreground'
              }`}>
                {isTokenExpired
                  ? `Токен истёк: ${expiryLabel}`
                  : isExpiringSoon
                    ? `Истекает через ${daysUntilExpiry} дн.: ${expiryLabel}`
                    : `Токен до: ${expiryLabel}`}
              </p>
            )}
          </div>
        </div>

        {/* Тэги */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {footer && !selection && (
          // Кнопки в подвале не должны открывать статистику кликом по карточке.
          <div onClick={(e) => e.stopPropagation()}>{footer}</div>
        )}
      </CardContent>
    </Card>
  );
}
